// AdminShell — left-rail nav layout for all /admin/* routes (T130).
//
// Wraps every admin page with a sticky 208-px left rail that groups routes
// into five sections: Org · Quality · Access · Integrations · System.
// The <Outlet /> on the right renders the matched child route unchanged.
//
// Permission-gating (UX layer, backend enforces):
//   - Sections requiring org_admin are hidden for lower-privileged roles.
//   - Quality section (profiles + gates) is visible to project_admin and above.
//   - Role is read by decoding the JWT payload stored in localStorage; if no
//     token is present all sections are shown (dev-mode / no-auth fallback).
//
// Integration: App.tsx wraps admin routes in
//   <Route path="admin" element={<AdminShell />}>…</Route>
// so this component sits between <Layout> (top nav) and each admin page.

import { Outlet, NavLink } from 'react-router-dom';
import type { LucideIcon } from 'lucide-react';
import {
  Activity,
  BarChart3,
  Bell,
  Bot,
  BookOpen,
  ClipboardList,
  ExternalLink,
  FileCode2,
  Key,
  KeyRound,
  Layers,
  Lock,
  Plug,
  Server,
  Settings,
  ShieldCheck,
  Sliders,
  Users,
  UserCog,
  Webhook,
} from 'lucide-react';
import { decodeJWTClaims } from '../lib/auth';

// ---------------------------------------------------------------------------
// Role helpers
// ---------------------------------------------------------------------------

const ROLE_RANK: Record<string, number> = {
  reader: 0,
  developer: 1,
  maintainer: 2,
  project_admin: 3,
  org_admin: 4,
};

function hasRole(userRole: string | undefined, required: string): boolean {
  return (ROLE_RANK[userRole ?? ''] ?? -1) >= (ROLE_RANK[required] ?? 999);
}

// ---------------------------------------------------------------------------
// Nav data
// ---------------------------------------------------------------------------

interface NavItem {
  /** React Router path for in-app navigation. */
  to?: string;
  /** External href — renders as <a target="_blank"> instead of NavLink. */
  href?: string;
  label: string;
  Icon: LucideIcon;
}

interface NavSection {
  id: string;
  label: string;
  /** Minimum role required to see this section. */
  minRole: string;
  items: NavItem[];
}

const NAV_SECTIONS: NavSection[] = [
  {
    id: 'org',
    label: 'Org',
    minRole: 'org_admin',
    items: [
      { to: '/admin/settings', label: 'Settings', Icon: Settings },
      { href: '/api/v1/docs', label: 'API', Icon: BookOpen },
    ],
  },
  {
    id: 'quality',
    label: 'Quality',
    minRole: 'project_admin',
    items: [
      { to: '/admin/quality-profiles', label: 'Quality Profiles', Icon: Layers },
      { to: '/admin/quality-gates', label: 'Quality Gates', Icon: ShieldCheck },
      { to: '/admin/rule-templates', label: 'Rule Templates', Icon: FileCode2 },
    ],
  },
  {
    id: 'access',
    label: 'Access',
    minRole: 'org_admin',
    items: [
      { to: '/admin/users', label: 'Users', Icon: Users },
      { to: '/admin/api-tokens', label: 'API Tokens', Icon: KeyRound },
      { to: '/admin/scim', label: 'SCIM', Icon: UserCog },
      { to: '/admin/sso', label: 'SSO', Icon: Lock },
    ],
  },
  {
    id: 'integrations',
    label: 'Integrations',
    minRole: 'org_admin',
    items: [
      { to: '/admin/integrations', label: 'Integrations', Icon: Plug },
      { to: '/admin/webhooks', label: 'Webhooks', Icon: Webhook },
      { to: '/admin/notifications', label: 'Notifications', Icon: Bell },
    ],
  },
  {
    id: 'system',
    label: 'System',
    minRole: 'org_admin',
    items: [
      { to: '/admin/system', label: 'System Health', Icon: Server },
      { to: '/admin/background-tasks', label: 'Background Tasks', Icon: Activity },
      { to: '/admin/features', label: 'Features', Icon: Sliders },
      { to: '/admin/license', label: 'License', Icon: Key },
      { to: '/admin/usage', label: 'Usage', Icon: BarChart3 },
      { to: '/admin/agents', label: 'Agents', Icon: Bot },
      { to: '/admin/audit', label: 'Audit Log', Icon: ClipboardList },
    ],
  },
];

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function AdminShell() {
  const claims = decodeJWTClaims();
  const role = claims?.role;

  // When role is unknown (no JWT), show everything — the backend enforces
  // access on every request and will 403 gracefully.
  const visibleSections =
    role === undefined
      ? NAV_SECTIONS
      : NAV_SECTIONS.filter((s) => hasRole(role, s.minRole));

  return (
    <div className="flex" style={{ minHeight: 'calc(100vh - 3.5rem)' }}>

      {/* Left rail — sticky, scrollable independent of content */}
      <aside
        className="
          w-52 shrink-0 border-r border-border bg-bg-elevated/60
          sticky overflow-y-auto
        "
        style={{ top: '3.5rem', height: 'calc(100vh - 3.5rem)' }}
        aria-label="Admin navigation"
      >
        <nav className="p-3 pt-4" aria-label="Admin sections">

          <p className="orb-eyebrow px-2 mb-3">Admin</p>

          {visibleSections.length === 0 ? (
            <p className="px-2 text-xs text-fg-subtle leading-snug">
              No admin access for your current role. Contact your org admin.
            </p>
          ) : (
            <div className="space-y-4">
              {visibleSections.map((section) => (
                <section key={section.id} aria-label={`${section.label} section`}>
                  <p className="orb-section-title px-2 mb-1">{section.label}</p>
                  <ul className="space-y-0.5">
                    {section.items.map((item) => (
                      <li key={item.to ?? item.href}>
                        {item.href ? (
                          <a
                            href={item.href}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-2 w-full px-2 py-1.5 rounded-md text-xs font-medium transition-colors text-fg-muted hover:text-fg hover:bg-bg-hover border border-transparent"
                          >
                            <item.Icon className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                            {item.label}
                            <ExternalLink className="h-3 w-3 ml-auto shrink-0 opacity-40" aria-hidden="true" />
                          </a>
                        ) : (
                          <NavLink
                            to={item.to!}
                            className={({ isActive }) =>
                              [
                                'flex items-center gap-2 w-full px-2 py-1.5 rounded-md',
                                'text-xs font-medium transition-colors',
                                isActive
                                  ? 'bg-accent/10 text-accent border border-accent/20'
                                  : 'text-fg-muted hover:text-fg hover:bg-bg-hover border border-transparent',
                              ].join(' ')
                            }
                          >
                            <item.Icon className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                            {item.label}
                          </NavLink>
                        )}
                      </li>
                    ))}
                  </ul>
                </section>
              ))}
            </div>
          )}

        </nav>
      </aside>

      {/* Main content — the matched admin child route renders here */}
      <div className="flex-1 min-w-0">
        <Outlet />
      </div>

    </div>
  );
}
